import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Users, Brain, History, Globe, FileCheck, Trash2, LogOut, MessageCircle } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import './TrustCenter.css';
import { AccessEntry, areaLabel, actorLabel, actionLabel, whenLabel } from './accessLogText';

// ============================================================
// INVESTMENTER TRUST CENTER — "Ihre Daten. Ihre Kontrolle." (Michael, 2026-10-15)
//
// "Vertrauen beweisbar machen, statt es nur zu behaupten." Every section shows
// what the system ACTUALLY records or does:
//   - access: the roles that can see the data (need-to-know), the named advisor;
//   - the AI: what it uses, and what it has stored from the owner's own
//     conversations (brain_profiles) — deletable here;
//   - access history: the owner's own sessions and login log (security module),
//     with "end session";
//   - external partners: the services the backend really sends data to,
//     checked in the code on 2026-10-15 — keep this list in step with it;
//   - consents on record (gdpr_consent_at, terms / privacy versions).
// Staff access: Backend/services/staffAccessLog.js logs every successful staff
// request that concerns this investor; the list below shows those entries.
// ============================================================

interface Props {
  investorId?: string;
  apiUrl: string;
  demoMode?: boolean;
  /** Opens the InvestMenter conversation (data copy / deletion requests). */
  onAsk: (message: string) => void;
}

const MEMORY_LABELS: Record<string, [string, string]> = {
  goal: ['Ziel', 'Goal'], budgetAmount: ['Budget', 'Budget'], budgetCurrency: ['Währung', 'Currency'],
  timeline: ['Zeitrahmen', 'Timeline'], financing: ['Finanzierung', 'Financing'], experience: ['Erfahrung', 'Experience'],
  strategy: ['Vermietungsstrategie', 'Rental strategy'], areas: ['Lagen', 'Areas'], propertyType: ['Immobilientyp', 'Property type'],
  bedrooms: ['Zimmer', 'Bedrooms'], concerns: ['Bedenken', 'Concerns'], motivation: ['Motivation', 'Motivation'],
  taxResidency: ['Steuerlicher Wohnsitz', 'Tax residency'], decisionMakers: ['Entscheidet mit', 'Decides with'],
  contactPreference: ['Kontaktwunsch', 'Contact preference'], mindset: ['Einschätzung', 'Mindset'], notes: ['Notizen', 'Notes'],
};

const TrustCenter: React.FC<Props> = ({ investorId, apiUrl, demoMode, onAsk }) => {
  const { language } = useLanguage();
  const de = language === 'de';
  const locale = de ? 'de-DE' : 'en-GB';
  const when = (raw: any) => {
    const d = raw ? new Date(raw._seconds ? raw._seconds * 1000 : raw) : null;
    return d && !isNaN(d.getTime())
      ? d.toLocaleString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '—';
  };

  const [me, setMe] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [staffLog, setStaffLog] = useState<AccessEntry[]>([]);
  const [loading, setLoading] = useState(!demoMode);
  const [busy, setBusy] = useState<string | null>(null);
  // Long lists start short; the owner opens and closes them.
  const [openStaff, setOpenStaff] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const SHORT = 3;
  const moreLess = (total: number, open: boolean, toggle: () => void) => total > SHORT ? (
    <button type="button" className="tc-more" onClick={toggle}>
      {open
        ? (de ? 'Weniger anzeigen' : 'Show less')
        : (de ? `Alle ${total} anzeigen` : `Show all ${total}`)}
    </button>
  ) : null;

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` });
  const load = useCallback(async () => {
    if (demoMode || !investorId || !apiUrl) { setLoading(false); return; }
    const id = encodeURIComponent(investorId);
    const get = (path: string) => fetch(`${apiUrl}${path}`, { headers: auth() }).then(r => (r.ok ? r.json() : null)).catch(() => null);
    const [m, h, s, a] = await Promise.all([get('/trust/me'), get(`/security/history/${id}?limit=15`), get(`/security/sessions/${id}`), get('/trust/access-log')]);
    if (a?.success) setStaffLog(a.entries || []);
    if (m?.success) setMe(m);
    if (h?.success) setHistory(h.history || []);
    if (s?.success) setSessions(s.sessions || []);
    setLoading(false);
  }, [apiUrl, investorId, demoMode]);
  useEffect(() => { load(); }, [load]);

  const deleteMemory = async () => {
    if (!window.confirm(de ? 'Alles löschen, was die Asset Desk KI aus Ihren Gesprächen gespeichert hat?' : 'Delete everything Asset Desk AI has stored from your conversations?')) return;
    setBusy('memory');
    await fetch(`${apiUrl}/trust/ai-memory`, { method: 'DELETE', headers: auth() }).catch(() => null);
    setBusy(null);
    load();
  };
  const endSession = async (sessionId: string) => {
    setBusy(sessionId);
    await fetch(`${apiUrl}/security/sessions/terminate`, {
      method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, investorId }),
    }).catch(() => null);
    setBusy(null);
    load();
  };

  const device = (x: any) => {
    const d = x?.deviceInfo;
    if (!d) return de ? 'Unbekanntes Gerät' : 'Unknown device';
    if (typeof d === 'string') return d;
    return [d.browser, d.os, d.device || d.platform].filter(Boolean).join(' · ') || (de ? 'Gerät' : 'Device');
  };
  const action = (a: string) => ({
    login_success: de ? 'Anmeldung' : 'Sign-in',
    login_failed: de ? 'Fehlgeschlagene Anmeldung' : 'Failed sign-in',
    logout: de ? 'Abmeldung' : 'Sign-out',
    new_device: de ? 'Neues Gerät' : 'New device',
    suspicious_login: de ? 'Auffällige Anmeldung' : 'Unusual sign-in',
    remote_logout: de ? 'Sitzung beendet' : 'Session ended',
  } as Record<string, string>)[a] || a;

  const memory = me?.aiMemory?.profile ? Object.entries(me.aiMemory.profile).filter(([, v]) => v) : [];

  const PARTNERS: [string, string, string][] = [
    ['Google Cloud / Firebase', 'Betrieb der App und Datenbank', 'Running the app and the database'],
    ['Google Gemini', 'KI: Gespräche mit der Asset Desk KI und das Lesen hochgeladener Dokumente', 'AI: conversations with Asset Desk AI and reading uploaded documents'],
    ['Google Drive', 'Ablage Ihrer Dokumente', 'Storage of your documents'],
    ['Close', 'Kundenbetreuung (CRM) Ihres Beraters', "Your advisor's client management (CRM)"],
    ['Slack', 'Interne Benachrichtigung unseres Teams, z. B. wenn Sie ein Gespräch wünschen', 'Internal team notification, e.g. when you ask for a call'],
    ['WATI (WhatsApp)', 'Nachrichten mit Ihrem Berater über WhatsApp', 'Messages with your advisor via WhatsApp'],
    ['E-Mail-Anbieter (SMTP)', 'Versand von Codes und Benachrichtigungen', 'Sending codes and notifications'],
    ['Notion', 'Interne Objektübersicht unseres Teams', "Our team's internal unit records"],
    ['Dubai Sell', 'Nur wenn Sie eine Einheit anbieten oder anfragen', 'Only if you list a unit or send an enquiry'],
  ];

  return (
    <div className="tc">
      <div className="tc-hero">
        <span className="tc-mark"><ShieldCheck size={20} /></span>
        <div className="tc-eyebrow">Asset Desk Trust Center</div>
        <div className="tc-title">{de ? 'Ihre Daten. Ihre Kontrolle.' : 'Your data. Your control.'}</div>
        <div className="tc-lead">
          {de
            ? 'Wir möchten nicht nur, dass Sie uns Ihre Vermögensdaten anvertrauen. Sie sollen jederzeit nachvollziehen können, warum dieses Vertrauen gerechtfertigt ist.'
            : 'We do not just want you to trust us with your wealth data. We want you to see at any time why that trust is justified.'}
        </div>
      </div>

      {/* 1 — access */}
      <section className="tc-card">
        <div className="tc-h"><Users size={18} /> {de ? 'Wer hat Zugriff auf Ihre Daten?' : 'Who has access to your data?'}</div>
        <ul className="tc-list">
          <li><b>{de ? 'Sie' : 'You'}</b> — {de ? 'vollständig.' : 'everything.'}</li>
          <li><b>{de ? 'Ihr Berater' : 'Your advisor'}{me?.advisor ? `: ${me.advisor}` : ''}</b> — {de ? 'Portfolio und Dokumente, um Sie zu betreuen.' : 'portfolio and documents, to look after you.'}</li>
          <li><b>{de ? 'Property-Management-Team' : 'Property management team'}</b> — {de ? 'nur die Einheiten, die wir für Sie verwalten.' : 'only the units we manage for you.'}</li>
          <li><b>{de ? 'Technische Administration' : 'Technical administration'}</b> — {de ? 'nur wenn es für Betrieb oder Support nötig ist.' : 'only when needed for operation or support.'}</li>
        </ul>
        <div className="tc-note">
          {de
            ? 'Grundsatz: Zugriff nur, wenn er für die jeweilige Aufgabe nötig ist (Need-to-know). Jeder Zugriff unseres Teams auf Ihre Daten wird protokolliert:'
            : 'Principle: access only when a task requires it (need-to-know). Every access by our team to your data is logged:'}
        </div>
        <div className="tc-sub">{de ? 'Wer aus unserem Team Ihre Daten geöffnet hat' : 'Who in our team opened your data'}</div>
        {loading ? <div className="tc-muted">…</div> : staffLog.length === 0 ? (
          <div className="tc-muted">{de ? 'Bisher keine Zugriffe durch unser Team protokolliert.' : 'No access by our team logged so far.'}</div>
        ) : (
          <>
            {(openStaff ? staffLog : staffLog.slice(0, SHORT)).map((e, i) => (
              <div key={`${e.at}-${i}`} className="tc-row">
                <div><b>{actorLabel(e, de)}</b><span>{actionLabel(e, de)}: {areaLabel(e.area, de)} · {whenLabel(e.at, de)}</span></div>
              </div>
            ))}
            {moreLess(staffLog.length, openStaff, () => setOpenStaff(v => !v))}
          </>
        )}
      </section>

      {/* 2 — the AI */}
      <section className="tc-card">
        <div className="tc-h"><Brain size={18} /> {de ? 'Was verwendet die Asset Desk KI?' : 'What does Asset Desk AI use?'}</div>
        <div className="tc-p">
          {de
            ? 'Ihre Einheiten und Kaufpreise, die DLD-Bewertungen, Ihre erfassten Mietverträge, Ihre eigenen Angaben unter „Mein Gesamtvermögen“ (falls erfasst) sowie öffentliche Marktdaten (DLD-Transaktionen, Dubai-Sell-Angebote). Die KI entscheidet nichts für Sie und führt keine Transaktionen aus.'
            : 'Your units and purchase prices, the DLD valuations, your recorded leases, your own entries under "My total wealth" (if any), and public market data (DLD transactions, Dubai Sell listings). The AI decides nothing for you and executes no transactions.'}
        </div>
        <div className="tc-sub">{de ? 'Was die KI aus Ihren Gesprächen gespeichert hat' : 'What the AI has stored from your conversations'}</div>
        {loading ? <div className="tc-muted">…</div> : memory.length === 0 ? (
          <div className="tc-muted">{de ? 'Nichts gespeichert.' : 'Nothing stored.'}</div>
        ) : (
          <>
            <div className="tc-kv">
              {memory.map(([k, v]) => (
                <div key={k}><span>{MEMORY_LABELS[k] ? MEMORY_LABELS[k][de ? 0 : 1] : k}</span><b>{String(v)}</b></div>
              ))}
            </div>
            <div className="tc-muted">{de ? 'Zuletzt aktualisiert:' : 'Last updated:'} {when(me?.aiMemory?.updatedAt)}</div>
            <button type="button" className="tc-btn is-danger" disabled={busy === 'memory'} onClick={deleteMemory}>
              <Trash2 size={14} /> {de ? 'KI-Gedächtnis löschen' : 'Delete AI memory'}
            </button>
          </>
        )}
      </section>

      {/* 3 — access history */}
      <section className="tc-card">
        <div className="tc-h"><History size={18} /> {de ? 'Wann wurde auf Ihr Konto zugegriffen?' : 'When was your account accessed?'}</div>
        <div className="tc-sub">{de ? 'Aktive Sitzungen' : 'Active sessions'}</div>
        {loading ? <div className="tc-muted">…</div> : sessions.length === 0 ? (
          <div className="tc-muted">{de ? 'Keine weiteren aktiven Sitzungen gefunden.' : 'No other active sessions found.'}</div>
        ) : sessions.map((s: any) => (
          <div key={s.sessionId} className="tc-row">
            <div><b>{device(s)}</b><span>{de ? 'Zuletzt aktiv' : 'Last active'} {when(s.lastActiveAt)}{s.ip ? ` · IP ${s.ip}` : ''}</span></div>
            <button type="button" className="tc-btn" disabled={busy === s.sessionId} onClick={() => endSession(s.sessionId)}>
              <LogOut size={14} /> {de ? 'Beenden' : 'End'}
            </button>
          </div>
        ))}
        <div className="tc-sub">{de ? 'Letzte Anmeldungen' : 'Recent sign-ins'}</div>
        {loading ? <div className="tc-muted">…</div> : history.length === 0 ? (
          <div className="tc-muted">{de ? 'Keine Einträge.' : 'No entries.'}</div>
        ) : (
          <>
            {(openHistory ? history : history.slice(0, SHORT)).map((h: any, i: number) => (
              <div key={h.logId || i} className={`tc-row ${h.isSuspicious ? 'is-flag' : ''}`}>
                <div><b>{action(h.action)}</b><span>{when(h.timestamp)} · {device(h)}{h.ip ? ` · IP ${h.ip}` : ''}</span></div>
              </div>
            ))}
            {moreLess(history.length, openHistory, () => setOpenHistory(v => !v))}
          </>
        )}
        <div className="tc-note">
          {de ? 'Etwas sieht nicht nach Ihnen aus? Beenden Sie die Sitzung und ändern Sie Ihr Passwort, oder sprechen Sie uns direkt an.' : 'Something does not look like you? End the session and change your password, or contact us directly.'}
        </div>
      </section>

      {/* 4 — partners */}
      <section className="tc-card">
        <div className="tc-h"><Globe size={18} /> {de ? 'Welche externen Partner verarbeiten Daten?' : 'Which external partners process data?'}</div>
        <div className="tc-partners">
          {PARTNERS.map(([name, deText, enText]) => (
            <div key={name}><b>{name}</b><span>{de ? deText : enText}</span></div>
          ))}
        </div>
        <div className="tc-note">{de ? 'Keine Weitergabe an Dritte zu Werbezwecken.' : 'No sharing with third parties for advertising.'}</div>
      </section>

      {/* 5 — consents */}
      <section className="tc-card">
        <div className="tc-h"><FileCheck size={18} /> {de ? 'Welche Einwilligungen haben Sie erteilt?' : 'What have you consented to?'}</div>
        {loading ? <div className="tc-muted">…</div> : (
          <div className="tc-kv">
            <div><span>{de ? 'AGB & Datenschutz akzeptiert' : 'Terms & privacy accepted'}</span><b>{me?.consent?.acceptedAt ? when(me.consent.acceptedAt) : (de ? 'nicht erfasst' : 'not on record')}</b></div>
            {me?.consent?.termsVersion && <div><span>{de ? 'AGB-Version' : 'Terms version'}</span><b>{me.consent.termsVersion}</b></div>}
            {me?.consent?.privacyVersion && <div><span>{de ? 'Datenschutz-Version' : 'Privacy version'}</span><b>{me.consent.privacyVersion}</b></div>}
            <div><span>{de ? 'Zwei-Faktor-Anmeldung' : 'Two-factor sign-in'}</span><b>{me?.twoFactorEnabled ? (de ? 'aktiv' : 'on') : (de ? 'nicht aktiv' : 'off')}</b></div>
          </div>
        )}
      </section>

      {/* 6 — rights */}
      <section className="tc-card">
        <div className="tc-h"><ShieldCheck size={18} /> {de ? 'Ihre Rechte' : 'Your rights'}</div>
        <div className="tc-p">
          {de
            ? 'Sie können jederzeit eine Kopie Ihrer Daten oder deren Löschung verlangen, soweit keine gesetzliche Aufbewahrungspflicht besteht. Niemand soll in seinen eigenen Vermögensdaten gefangen sein.'
            : 'You can ask for a copy of your data or its deletion at any time, unless the law requires us to keep it. No one should be trapped in their own wealth data.'}
        </div>
        <button type="button" className="tc-btn is-gold"
          onClick={() => onAsk(de ? 'Ich möchte eine Kopie meiner Daten bzw. deren Löschung anfragen.' : 'I would like to request a copy of my data or its deletion.')}>
          <MessageCircle size={14} /> {de ? 'Datenkopie oder Löschung anfragen' : 'Request a data copy or deletion'}
        </button>
      </section>
    </div>
  );
};

export default TrustCenter;
